
import { summariesApi } from '@/api';
import { PlainDate } from '@/lib/date-time/PlainDate';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { isWorkingDay } from '@/lib/workPatternFunctions';
import store from '@/store';
import { startOfDay, startOfNextDay } from '@/util/dateArithmetic';
import spacetime from 'spacetime';
import { Absence, Employee, Unavailability } from '../../../../../api/v1';
import InlineDialog from '../../../../components/info/InlineDialog.vue';
import {
  isZeroHourEmployee,
  needsToConfirmAccount,
} from '../../../../lib/employee/employeeFunctions';
import { Icon, IconMap } from '../../../../lib/enum/Icon';
import { Entity } from '../../../../lib/realtime/RealtimeEntity';

const { timezone } = store.getters;

export default {
  name: 'ShiftActionWarnings',

  components: { InlineDialog },

  props: {
    employee: {
      type: Object as () => Employee,
      required: true,
    },
    startDate: {
      type: Date,
      required: true,
    },
    endDate: {
      type: Date,
      required: true,
    },
    showWorkingHoursWarning: {
      type: Boolean,
      default: false,
    },
    additionalPaidMinutes: {
      type: Number,
      default: 0,
    },
  },

  data() {
    const start = startOfDay(this.startDate, timezone);
    const end = startOfNextDay(this.endDate, timezone);
    const startDate = PlainDate.fromDate(this.startDate, timezone);
    const endDate = PlainDate.fromDate(this.endDate, timezone);
    return {
      Icon,
      IconMap,
      employeeWeekTotalWorkedMinutes: 0,
      workPatterns: realtimeQuery(Entity.WorkPattern).fetch(),
      workPatternOverrides: realtimeQuery(Entity.WorkPatternOverride)
        .whereDateInPeriod('date', startDate, endDate)
        .where('employeeId', 'eq', this.employee.id)
        .fetch(),
      absencesClosed: realtimeQuery(Entity.Absence)
        .whereDatesOverlap('startedOn', 'endedOn', startDate, endDate)
        .fetch(),
      absencesOpen: realtimeQuery(Entity.Absence)
        .where('startedOn', 'lte', endDate)
        .whereNull('endedOn')
        .fetch(),
      unavailability: realtimeQuery(Entity.Unavailability)
        .whereDatesTimesOverlap('startsAt', 'endsAt', start, end)
        .fetch(),
      shifts: realtimeQuery(Entity.Shift)
        .whereDatesTimesOverlap('startsAt', 'endsAt', start, end)
        .fetch(),
    };
  },

  computed: {
    startOfWeek: () => store.state.settings.companySettings.startOfWeek,
    timezone: () => store.getters.timezone,

    employeeAbsence(): Absence[] {
      return this.absencesOpen.data.filter(belongsToEmployee(this.employee.id));
    },
    employeeUnavailability(): Unavailability[] {
      return this.unavailability.data.filter(
        (u) => u.employeeId === this.employee.id,
      );
    },
    employeeWorksDate() {
      const workPattern = this.workPatterns.data.find(
        (p) => p.id === this.employee.workPatternId,
      );
      if (!workPattern) return true;
      return isWorkingDay(
        workPattern,
        this.workPatternOverrides.data,
        this.startDate,
        this.timezone,
      );
    },
    calculateRemainingWorkableMinutes() {
      const employeeMaxMinutes = this.employee.workingHoursPerWeek * 60;

      return (
        employeeMaxMinutes -
        this.employeeWeekTotalWorkedMinutes -
        this.additionalPaidMinutes
      );
    },
  },

  async created() {
    await this.getEmployeeWeekTotalWorkedMinutes();
  },

  methods: {
    isZeroHourEmployee,
    needsToConfirmAccount,

    calculateHours(hours: number): string {
      const durationInSeconds = hours * 60 * 60;

      const wholeHours = Math.floor(durationInSeconds / 3600);
      const wholeMinutes = Math.floor(
        (durationInSeconds - wholeHours * 3600) / 60,
      );
      return `${wholeHours}h ${String(wholeMinutes).padStart(2, '0')}m`;
    },

    async getEmployeeWeekTotalWorkedMinutes() {
      const date = spacetime(this.startDate, this.timezone)
        .startOf('day')
        .weekStart(this.startOfWeek);
      const startDate = date.startOf('week').toNativeDate();
      const endDate = date
        .endOf('week')
        .add(1, 'day')
        .startOf('day')
        .toNativeDate();

      await summariesApi
        .showEmployeeWeeklyShiftsTotal({
          id: this.employee.id,
          startDate,
          endDate,
        })
        .then(({ data }) => {
          this.employeeWeekTotalWorkedMinutes = data.mins;
        })
        .catch(() => {
          this.employeeWeekTotalWorkedMinutes = 0;
        });
    },
  },
};
